import style from './style.module.scss'
import {Button, EmptyImg, Filter, OptionButton, PageTitle, Table} from "@/theme";
import cn from "classnames/bind";
import {useEffect, useRef, useState} from "react";
import {useApi, useApiMutation, useTempData} from "@/lib/hook";
import {Spin} from "antd";
import {DeleteOutlined, EyeOutlined} from "@ant-design/icons";
import {paginationHelper} from "@/lib/utils";
import {debounce} from "@/lib/utils/debounce";
import {api} from "@/lib/api/api";
import {NewsEditModal} from "@/components/catnip/modal/newsEditModal";
import {NewsCreateModal} from "@/components/catnip/modal/newsCreateModal";
import {NewsShowModal} from "@/components/catnip/modal/newsShowModal";

const initFilterData = {
    page: 1,
}

export const CatNipManagement = () => {
    const {data: tempData, setTempData} = useTempData('catnip_page', initFilterData);
    const [catNipInput, setCatNipInput] = useState(null)
    const debounceStateRef = useRef({lastCall: 0, lastCallTimer: null});
    const [isDirty, setIsDirty] = useState(false);

    const {data, isFetching, refetch} = useApi({
        route: {name: 'catnip.news.list'},
        data: {
            page: tempData.page,
            filter: tempData.filter
        }
    })

    const {data: catNipData} = useApi({
        route: {name: 'catnip.get'},
        setting: {
            onSuccess: (res) => {
                console.log("RES>>", res)
                setCatNipInput({
                    title: res?.data?.title,
                    sub_title: res?.data?.sub_title,
                })
            },
        }
    })

    const columns = [
        {
            title: 'Picture', width: '80px',
            render: (item) => (
                <>
                    {item?.file_path ?
                        <img src={item?.file_path} className={style.preview} alt="news-preview"/>
                        :
                        <div className={style.preview}>
                            <EmptyImg/>
                        </div>
                    }
                </>
            )
        },
        {
            title: 'Title', width: '180px',
            render: (item) => (
                <>
                    <div className={style.title}>
                        {item.title}
                    </div>
                </>
            )
        },
        {
            title: 'Text', width: '550px',
            render: (item) => (
                <>
                    <div
                        className={style.text}
                        dangerouslySetInnerHTML={{__html: item.text}}
                    />
                </>
            )
        },
        {
            title: 'Author', width: '176px',
            render: (item) => (
                <>
                    <div className={style.author}>
                        {item.author}
                    </div>
                </>
            )
        },
        {
            title: 'Actions', width: '112px', className: 'tl_center',
            render: (item) => (
                <div
                    className={style.actions_cell}
                >
                    <Button
                        icon={<EyeOutlined/>}
                        type={'text'}
                        onClick={() => {
                            showNews(item)
                        }}
                    />
                    <OptionButton>
                        <OptionButton.Button
                            onClick={() => {
                                showNewsEdit(item)
                            }}
                        >
                            Edit
                        </OptionButton.Button>
                        <OptionButton.Button
                            onClick={() => {
                                onDeleteNews(item.id)
                            }}
                            confirm={{
                                title: `Are you sure you want to delete News ?`,
                                content: 'If you delete a news, all data about him will be irretrievably lost',
                                type: 'delete',
                                okButtonProps: {
                                    type: 'primary',
                                    icon: <DeleteOutlined/>,
                                }
                            }}
                            type={'danger'}
                        >
                            Delete
                        </OptionButton.Button>
                    </OptionButton>
                </div>
            )
        },

    ]

    const onChangePage = (page) => {
        setTempData({
            page,
            filter: {
                ...tempData.filter
            }
        })
    }
    const onChangeFilter = (value) => {
        setTempData({
            page: 1,
            filter: {
                ...tempData.filter,
                ...value,
            }
        })
    }

    const updateData = (data) => {
        api.send({
            route: {name: 'catnip.update'},
            data: {...data}
        }).catch((error) => {
            console.error('Error while update data:', error);
        });
    };

    const debounceRequest = useRef(
        debounce(updateData, 1000, debounceStateRef)
    ).current;

    useEffect(() => {
        if (isDirty && catNipInput) {
            debounceRequest(catNipInput);
        }

        return () => {
            if (debounceStateRef.current?.lastCallTimer) {
                clearTimeout(debounceStateRef.current.lastCallTimer);
            }
        };
    }, [catNipInput]);

    const newsDelete = useApiMutation({
        onSuccess: () => {
            refetch()
        }
    })

    const onDeleteNews = (id) => {
        newsDelete.mutate({
            route: {name: 'catnip.news.delete', query: {id}}
        })
    }

    const [modalShow, setModalShow] = useState({
        open: false,
        news: null
    })

    const [modalCreate, setModalCreate] = useState({
        open: false,
    })

    const [modalEditSetting, setModalEditSetting] = useState({
        open: false,
        news: null
    })

    const showNews = (news) => {
        setModalShow((prev) => ({
            open: !prev.open,
            news,
        }))
    }

    const showNewsEdit = (news) => {
        setModalEditSetting((prev) => ({
            open: !prev.open,
            news,
        }))
    }

    const showNewsCreate = () => {
        setModalCreate((prev) => ({
            open: !prev.open,
        }))
    }

    const closeNewsModalShow = () => {
        setModalShow((prev) => ({
            open: !prev.open,
            news: null,
        }))
    }

    const closeNewsModalEdit = () => {
        setModalEditSetting((prev) => ({
            open: !prev.open,
            news: null,
        }))
    }

    const closeNewsModalCreate = () => {
        setModalCreate((prev) => ({
            open: !prev.open
        }))
    }

    return (
        <>
            <PageTitle
                title={'CatNip Management'}
            />

            <Spin spinning={isFetching}>

                <div className={style.cat_nip_header}>
                    <h3>CatNip Editor</h3>
                    <div className={style.inputsWrapper}>
                        <div className={style.labelsRow}>
                            <label className={style.inputLabel} htmlFor="title">
                                Section title
                            </label>
                            <label className={style.inputLabel} htmlFor="subtitle">
                                Subtitle
                            </label>
                        </div>
                        <div className={style.inputsRow}>
                            <input
                                id="title"
                                type="text"
                                value={catNipInput?.title || ''}
                                onChange={(e) => {
                                    setIsDirty(true);
                                    setCatNipInput({...catNipInput, title: e.target.value})
                                }}
                                className={style.inputField}
                                placeholder="Enter section title"
                            />
                            <input
                                id="subtitle"
                                type="text"
                                value={catNipInput?.sub_title || ''}
                                onChange={(e) => {
                                    setIsDirty(true);
                                    setCatNipInput({...catNipInput, sub_title: e.target.value})
                                }}
                                className={style.inputField}
                                placeholder="Enter subtitle (max 100 chars)"
                            />
                        </div>
                    </div>
                </div>
                <h3>News & Stories</h3>
                <div className={style.head_table}>
                    <CatNipFilter
                        initialValues={tempData}
                        onChangeFilter={onChangeFilter}
                    />
                    <button onClick={() => showNewsCreate()}>Add new article</button>
                </div>

                <Table
                    className={style.table}
                    style={{
                        marginTop: 16,
                    }}
                    dataSource={data?.data}
                    columns={columns}
                    rowKey={'id'}
                    pagination={data?.meta ? paginationHelper(data.meta, onChangePage) : false}
                />
            </Spin>

            <NewsShowModal
                isModalOpen={modalShow.open}
                setIsModalOpen={closeNewsModalShow}
                catNip={catNipInput}
                news={modalShow.news}
            />

            <NewsCreateModal
                isModalOpen={modalCreate.open}
                setIsModalOpen={closeNewsModalCreate}
                catNip={catNipInput}
                refetch={refetch}
            />

            <NewsEditModal
                isModalOpen={modalEditSetting.open}
                setIsModalOpen={closeNewsModalEdit}
                news={modalEditSetting.news}
                catNip={catNipInput}
                refetch={refetch}
            />


        </>
    )
}

const CatNipFilter = ({initialValues, onChangeFilter}) => {
    return (
        <Filter
            initialValues={initialValues}
            onChange={onChangeFilter}
            useFilters={[
                {
                    type: 'search',
                    name: 'q',
                    placeholder: 'Search by title, description...',
                    width: 368,
                }
            ]}
            wrapper={(children, level) => (
                <div className={cn(style.filter, {[style.main_level]: level === 1})}>
                    {children}
                </div>
            )}
        />
    )
}